import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { useTelegramLogin } from '../telegram';

/**
 * The Telegram half of the sign-in page.
 *
 * The verification code is the security-relevant part of this UI: the user is
 * told, before they open the bot, which code they should see there. If the codes
 * differ, someone else started the sign-in and they should decline.
 */
export function TelegramLoginPanel({ onSignedIn }: { onSignedIn?: () => void }) {
  const { state, start, reset } = useTelegramLogin(onSignedIn);

  if (state.phase === 'idle') {
    return (
      <Button type="button" variant="secondary" className="w-full" onClick={() => void start()}>
        <TelegramGlyph />
        Telegram ile giriş yap
      </Button>
    );
  }

  if (state.phase === 'starting') {
    return (
      <Button type="button" variant="secondary" className="w-full" loading>
        Hazırlanıyor…
      </Button>
    );
  }

  if (state.phase === 'failed') {
    return (
      <div className="flex flex-col gap-3">
        <Alert tone="error">{state.error}</Alert>
        <Button type="button" variant="ghost" onClick={reset}>
          Tekrar dene
        </Button>
      </div>
    );
  }

  if (state.phase === 'approved') {
    return <Alert tone="success">Giriş onaylandı. Yönlendiriliyorsunuz…</Alert>;
  }

  if (state.phase === 'denied') {
    return (
      <div className="flex flex-col gap-3">
        <Alert tone="error">
          Giriş Telegram üzerinden reddedildi. Bu girişi siz başlatmadıysanız yapmanız gereken başka
          bir şey yok.
        </Alert>
        <Button type="button" variant="ghost" onClick={reset}>
          Yeniden başlat
        </Button>
      </div>
    );
  }

  if (state.phase === 'expired') {
    return (
      <div className="flex flex-col gap-3">
        <Alert tone="error">Bu giriş isteğinin süresi doldu.</Alert>
        <Button type="button" variant="secondary" onClick={() => void start()}>
          Yeniden başlat
        </Button>
      </div>
    );
  }

  if (state.phase === 'group_required') {
    return (
      <div className="flex flex-col gap-3">
        <Alert tone="error">Giriş yapabilmek için topluluk grubuna üye olmanız gerekiyor.</Alert>

        {state.groupInviteUrl ? (
          <a
            href={state.groupInviteUrl}
            target="_blank"
            rel="noreferrer noopener"
            className="rounded-lg bg-brand-500 px-4 py-2.5 text-center font-medium text-surface-950 transition hover:bg-brand-400"
          >
            Gruba katıl
          </a>
        ) : null}

        <p className="text-center text-xs text-surface-500">
          Katıldıktan sonra Telegram&apos;daki bağlantıya tekrar dokunun — bu sayfa kendiliğinden
          devam eder.
        </p>
      </div>
    );
  }

  // pending | awaiting_confirmation
  return (
    <div className="flex flex-col gap-4 rounded-xl border border-surface-800 bg-surface-900 p-5">
      <div className="text-center">
        <p className="text-xs uppercase tracking-widest text-surface-500">Doğrulama kodu</p>
        <p className="mt-1 font-mono text-3xl font-bold tracking-[0.3em] text-brand-400">
          {state.verificationCode}
        </p>
      </div>

      <p className="text-center text-sm leading-relaxed text-surface-400">
        Telegram&apos;ı açın ve bot&apos;un gösterdiği kodun{' '}
        <b className="text-surface-200">yukarıdakiyle aynı</b> olduğunu doğrulayıp onaylayın.
      </p>

      {state.deepLink ? (
        <a
          href={state.deepLink}
          target="_blank"
          rel="noreferrer noopener"
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-brand-500 px-4 py-2.5 font-medium text-surface-950 transition hover:bg-brand-400"
        >
          <TelegramGlyph />
          Telegram&apos;da aç
        </a>
      ) : null}

      <p
        className="flex items-center justify-center gap-2 text-xs text-surface-500"
        role="status"
        aria-live="polite"
      >
        <span
          aria-hidden
          className="size-3 animate-spin rounded-full border-2 border-surface-600 border-t-brand-400"
        />
        {state.phase === 'awaiting_confirmation'
          ? 'Onayınız bekleniyor…'
          : 'Telegram’da açmanız bekleniyor…'}
      </p>

      <button
        type="button"
        onClick={reset}
        className="text-xs text-surface-500 underline underline-offset-2 hover:text-surface-300"
      >
        Vazgeç
      </button>
    </div>
  );
}

function TelegramGlyph() {
  return (
    <svg aria-hidden viewBox="0 0 24 24" className="size-5 fill-current">
      <path d="M21.9 4.3 18.7 19.4c-.2 1-.9 1.3-1.7.8l-4.7-3.5-2.3 2.2c-.3.3-.5.5-1 .5l.3-4.8 8.8-8c.4-.3-.1-.5-.6-.2L6.6 12.2l-4.7-1.5c-1-.3-1-1 .2-1.5l18.3-7c.9-.3 1.6.2 1.5 1.1z" />
    </svg>
  );
}
